import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useRegister } from "../queries/authQueries";
import { useAuthStore } from "../stores/authStore";
import { ApiError } from "../lib/apiClient";
import { useMenuItems } from "../queries/menuQueries";
import { getMenuUrl } from "../utils/restaurant";
import {
  Container,
  Paper,
  Title,
  TextInput,
  PasswordInput,
  Button,
  Text,
  Anchor,
  Stack,
  Alert,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle } from "@tabler/icons-react";

export default function RegisterPage() {
  const { t } = useTranslation();
  const { mutate: register, isPending, error } = useRegister();
  const setCredentials = useAuthStore((state) => state.setCredentials);
  const navigate = useNavigate();
  const { data: menuItems } = useMenuItems(true);

  const form = useForm({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      password: "",
    },
    validate: {
      password: (value: string) =>
        value.length < 6 ? "Adgangskoden er for kort" : null,
    },
  });

  // Get restaurant ID from first menu item
  const restaurantId =
    menuItems && menuItems.length > 0
      ? (menuItems[0] as any).restaurant_id
      : undefined;

  const handleSubmit = form.onSubmit(
    (values: {
      name: string;
      email: string;
      phone: string;
      password: string;
    }) => {
      register(
        {
          email: values.email,
          password: values.password,
          name: values.name,
          phone: values.phone || undefined,
        },
        {
          onSuccess: (response) => {
            setCredentials(response.user, response.access_token);
            navigate(getMenuUrl(restaurantId));
          },
          onError: (err) => {
            console.error("Registration failed:", err);
          },
        }
      );
    }
  );

  const errorMessage =
    error instanceof ApiError &&
    typeof error.data === "object" &&
    error.data &&
    "detail" in error.data
      ? String(error.data.detail)
      : t("auth.register.error");

  return (
    <Container size="xs" py="xl">
      <Paper withBorder shadow="md" p={30} radius="md">
        <Title order={2} ta="center" mb="md">
          {t("auth.register.title")}
        </Title>

        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <TextInput
              label={t("auth.register.name")}
              autoComplete="name"
              {...form.getInputProps("name")}
            />

            <TextInput
              label={t("auth.register.email")}
              type="email"
              autoComplete="email"
              {...form.getInputProps("email")}
            />

            <TextInput
              label={t("auth.register.phone")}
              type="tel"
              autoComplete="tel"
              {...form.getInputProps("phone")}
            />

            <PasswordInput
              label={t("auth.register.password")}
              autoComplete="new-password"
              {...form.getInputProps("password")}
            />

            {error && (
              <Alert icon={<IconAlertCircle size={16} />} color="red">
                {errorMessage}
              </Alert>
            )}

            <Button type="submit" fullWidth color="brand" loading={isPending}>
              {t("auth.register.submit")}
            </Button>
          </Stack>
        </form>

        <Text ta="center" mt="md" size="sm">
          {t("auth.register.hasAccount")}{" "}
          <Anchor component={Link} to="/login" fw={500}>
            {t("auth.register.loginLink")}
          </Anchor>
        </Text>
      </Paper>
    </Container>
  );
}
